import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { X, AlertTriangle, Plus } from 'lucide-react'
import type { CasePriority, CaseQuotaStatus } from '../../types/case-management'
import { formatQuotaDenialReason } from '../../hooks/use-case-quota'
import { INPUT_STYLE, PRIORITY_COLORS, PRIORITY_VALUES } from './new-case-modal.constants'

export { ClientPicker } from './client-picker.parts'
export type { ClientDropdownPanelProps } from './client-picker.parts'

export function ModalHeader({ onClose }: { onClose?: (() => void) | undefined }): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div
      className="sticky top-0 z-10 flex items-center justify-between border-b px-6 py-4"
      style={{
        borderColor: 'var(--border, rgba(28,31,46,0.09))',
        background: 'var(--bg-card, #ffffff)',
      }}
    >
      <div>
        <h2
          className="text-xl font-bold"
          style={{
            color: 'var(--text, #1c1f2e)',
            fontFamily: 'var(--font-display, Syne, sans-serif)',
          }}
        >
          {t('newCase.title')}
        </h2>
        <p className="mt-0.5 text-sm" style={{ color: 'var(--text-muted, #8e93a6)' }}>
          {t('newCase.subtitle')}
        </p>
      </div>
      <button
        onClick={onClose}
        className="rounded-lg p-2 transition-colors"
        style={{ color: 'var(--text-muted, #8e93a6)' }}
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  )
}

export function QuotaWarning({ quotaStatus }: { quotaStatus: CaseQuotaStatus }): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="mx-6 mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
        <div>
          <p className="font-medium text-amber-700">{t('newCase.cannotCreate')}</p>
          <p className="mt-1 text-sm text-amber-600">
            {formatQuotaDenialReason(quotaStatus.denialReason)}
          </p>
        </div>
      </div>
    </div>
  )
}

export function QuotaIndicator({ quotaStatus }: { quotaStatus: CaseQuotaStatus }): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="mx-6 mt-4 flex items-center justify-between text-sm">
      <span style={{ color: 'var(--text-muted, #8e93a6)' }}>{t('newCase.casesRemaining')}</span>
      <span className="font-medium" style={{ color: 'var(--text-dim, #555c72)' }}>
        {quotaStatus.casesRemaining} of {quotaStatus.casesPerSeatLimit}
      </span>
    </div>
  )
}

export function CaseNameField({
  value,
  onChange,
  disabled,
}: {
  value: string
  onChange: (value: string) => void
  disabled: boolean
}): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div>
      <label
        htmlFor="case-name"
        className="mb-2 block text-sm font-medium"
        style={{ color: 'var(--text-dim, #555c72)' }}
      >
        {t('newCase.caseName')} <span className="text-red-500">*</span>
      </label>
      <input
        id="case-name"
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
        }}
        placeholder={t('newCase.caseNamePlaceholder')}
        className="w-full rounded-lg border px-4 py-2.5 transition-all focus:border-transparent focus:ring-2 focus:ring-[#4e5e82] focus:outline-none"
        style={INPUT_STYLE}
        disabled={disabled}
      />
    </div>
  )
}

export function OrgReferenceField({
  value,
  onChange,
  disabled,
}: {
  value: string
  onChange: (value: string) => void
  disabled: boolean
}): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div>
      <label
        htmlFor="org-ref"
        className="mb-2 block text-sm font-medium"
        style={{ color: 'var(--text-dim, #555c72)' }}
      >
        {t('newCase.orgReference')}
      </label>
      <input
        id="org-ref"
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
        }}
        placeholder={t('newCase.orgReferencePlaceholder')}
        className="w-full rounded-lg border px-4 py-2.5 font-mono text-sm transition-all focus:border-transparent focus:ring-2 focus:ring-[#4e5e82] focus:outline-none"
        style={INPUT_STYLE}
        disabled={disabled}
      />
      <p className="mt-1 text-xs" style={{ color: 'var(--text-muted, #8e93a6)' }}>
        {t('newCase.orgReferenceHint')}
      </p>
    </div>
  )
}

export function ScopeField({
  value,
  onChange,
  disabled,
}: {
  value: string
  onChange: (value: string) => void
  disabled: boolean
}): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div>
      <label
        htmlFor="scope"
        className="mb-2 block text-sm font-medium"
        style={{ color: 'var(--text-dim, #555c72)' }}
      >
        {t('newCase.scopeDescription')} <span className="text-red-500">*</span>
      </label>
      <textarea
        id="scope"
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
        }}
        placeholder={t('newCase.scopePlaceholder')}
        rows={4}
        className="w-full resize-none rounded-lg border px-4 py-2.5 transition-all focus:border-transparent focus:ring-2 focus:ring-[#4e5e82] focus:outline-none"
        style={INPUT_STYLE}
        disabled={disabled}
      />
    </div>
  )
}

function PriorityButton({
  value,
  active,
  disabled,
  onClick,
}: {
  value: CasePriority
  active: boolean
  disabled: boolean
  onClick: () => void
}): ReactElement {
  const { t } = useTranslation('cases')
  const labelKey = `priority.${value}` as const
  const descKey = `priority.${value}Desc` as const
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`relative flex items-start gap-3 rounded-lg border-2 p-3 transition-all ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
      style={{
        borderColor: active ? 'var(--accent, #4e5e82)' : 'var(--border, rgba(28,31,46,0.09))',
        background: active ? 'var(--accent-dim, rgba(78,94,130,0.1))' : 'transparent',
      }}
    >
      <span className={`mt-0.5 h-3 w-3 flex-shrink-0 rounded-full ${PRIORITY_COLORS[value]}`} />
      <div className="text-left">
        <span
          className="block text-sm font-medium"
          style={{ color: active ? 'var(--accent, #4e5e82)' : 'var(--text, #1c1f2e)' }}
        >
          {t(labelKey as never)}
        </span>
        <span className="mt-0.5 block text-xs" style={{ color: 'var(--text-muted, #8e93a6)' }}>
          {t(descKey as never)}
        </span>
      </div>
    </button>
  )
}

export function PriorityPicker({
  value,
  onChange,
  disabled,
}: {
  value: CasePriority
  onChange: (value: CasePriority) => void
  disabled: boolean
}): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div>
      <label
        className="mb-3 block text-sm font-medium"
        style={{ color: 'var(--text-dim, #555c72)' }}
      >
        {t('newCase.priorityLevel')}
      </label>
      <div className="grid grid-cols-2 gap-3">
        {PRIORITY_VALUES.map((priority) => (
          <PriorityButton
            key={priority}
            value={priority}
            active={value === priority}
            disabled={disabled}
            onClick={() => {
              onChange(priority)
            }}
          />
        ))}
      </div>
    </div>
  )
}

export function CriticalWarning(): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-3">
      <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
      <p className="text-sm text-red-700">{t('newCase.criticalWarning')}</p>
    </div>
  )
}

export function ModalActions({
  onClose,
  isValid,
  canCreateCase,
  isCreating,
}: {
  onClose?: (() => void) | undefined
  isValid: boolean
  canCreateCase: boolean
  isCreating: boolean
}): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div
      className="flex items-center justify-end gap-3 border-t pt-4"
      style={{ borderColor: 'var(--border, rgba(28,31,46,0.09))' }}
    >
      <button
        type="button"
        onClick={onClose}
        className="rounded-lg px-4 py-2.5 font-medium transition-colors"
        style={{ color: 'var(--text-dim, #555c72)' }}
      >
        {t('actions.cancel')}
      </button>
      <button
        type="submit"
        disabled={!isValid || !canCreateCase || isCreating}
        className="flex items-center gap-2 rounded-lg bg-[#4e5e82] px-6 py-2.5 font-medium text-white transition-colors hover:bg-[#5c6e94] disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isCreating ? (
          <>
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            {t('newCase.creating')}
          </>
        ) : (
          <>
            <Plus className="h-4 w-4" />
            {t('newCase.createCase')}
          </>
        )}
      </button>
    </div>
  )
}
